import { addQuestionElementOptions } from '../utils/addQuestionElementOptions';
import { call, fork, put, race, select, take } from 'redux-saga/effects';
import { API_URL, PPROXY_SERVER_URL } from '../../../config';
import {
  setFirstResponseTime,
  setQuestion,
  updateElement,
  saveIncompleteSurvey as saveIncompleteSurveyAction,
} from '../actions';
import { CHAT, THANK } from '../../FlowState/stateTypes';
import { DYNAMIC_MULTIPLE_CHOICE, MATRIX } from '../../../constants/questionTypes';
import { END, eventChannel } from 'redux-saga';
import { getOptionsFromSourceQuestion } from '../utils/getOptionsFromSourceQuestion';
import { isMatrixResponseComplete } from '../utils/matrixValidation';
import { NEXT_PAGE, QUESTION, SUBMIT } from '../../../components/FlowStateElement/elementTypes';
import { nextStateSaga, isQuestionsAnswered, findNextStateInfo } from './changeStateSaga';
import { LOAD_FLOW_SUCCESS, REMOVE_FILE, RESET_FLOW } from '../constants';
import { shouldReport } from '../../../utils/error';
import { requestJson } from '../../../utils/request';
import { assertOnline, isConnectionError } from '../utils/connection';
import { scrollToTargetElement } from '../helpers/scrollHelper';
import { SentryReport } from '../../../utils/sentry';
import * as AutoScrollElement from '../../../constants/autoScrollElements';
import findKey from 'lodash/findKey';
import formatBytes from '../../../utils/formatBytes';
import getFeedbackFromState from '../utils/getFeedbackFromState';
import identity from 'lodash/identity';
import conditionRulesEngine from '../utils/conditionRulesEngine';
import { submitFeedbackSaga } from './submitFeedbackSaga';

const TRIGGER_MAKE_VISIBLE = 'make_visible';
const TRIGGER_MAKE_INVISIBLE = 'make_invisible';
const TRIGGER_MAKE_REQUIRED = 'make_required';

function hasStateNextPageButton(state) {
  const { currentState, elements } = state.flow;
  return (
    currentState.elements
      .map((id) => elements[id])
      .filter((el) => (el.type === NEXT_PAGE || el.type === SUBMIT) && el.isVisible).length > 0
  );
}

function ruleTargetsElement(action, elementKey) {
  return (
    (action.target_element_keys || []).includes(elementKey) ||
    (action.target_option_keys || []).some((target) => target.question_key === elementKey) ||
    (action.target_statement_keys || []).some((target) => target.question_key === elementKey)
  );
}

function ruleTriggeredByElement(rule, elementKey) {
  return (rule.condition_groups || []).some((group) =>
    (group.conditions || []).some((condition) => {
      const triggerElementKey = condition.trigger_ref?.element_key || condition.element_key;
      return triggerElementKey === elementKey;
    }),
  );
}

function elementIsTargetedByChangedElement(conditionRules, changedElementKey, elementKey) {
  if (!changedElementKey || !elementKey) return false;

  return conditionRules.some((rule) => {
    const action = rule.action || {};
    return ruleTriggeredByElement(rule, changedElementKey) && ruleTargetsElement(action, elementKey);
  });
}

/**
 * Applies condition rules with cascading support.
 * When elements are hidden and their responses cleared, rules that depend on
 * those elements are re-evaluated to handle multi-level conditional visibility.
 *
 * @param {Object} state - The Redux state
 * @param {string|null} changedElementKey - The key of the element that triggered the change
 */
export function* applyConditionRules(state, changedElementKey = null) {
  const MAX_CASCADE_ITERATIONS = 10; // Prevent infinite loops
  let currentState = state;
  let keysToEvaluate = changedElementKey ? [changedElementKey] : null;
  let iteration = 0;

  while (iteration < MAX_CASCADE_ITERATIONS) {
    iteration++;
    const hiddenElementKeys = yield* applyConditionRulesOnce(
      currentState,
      keysToEvaluate ? keysToEvaluate[0] : null,
      keysToEvaluate,
    );

    if (hiddenElementKeys.length === 0) {
      // No more elements were hidden, we're done
      break;
    }

    // Get fresh state after changes and re-evaluate rules for hidden elements
    currentState = yield select();
    keysToEvaluate = hiddenElementKeys;
  }
}

/**
 * Single iteration of condition rules application.
 * Returns the keys of elements that were hidden and had responses cleared.
 */
function* applyConditionRulesOnce(state, changedElementKey = null, allChangedKeys = null) {
  const { conditionRules, elements, questions } = state.flow;
  const hiddenElementKeys = [];
  const shouldPreserveSelectedOptions = !allChangedKeys || allChangedKeys.length <= 1;

  if (!conditionRules || !Array.isArray(conditionRules) || conditionRules.length === 0) {
    return hiddenElementKeys;
  }

  // When we have multiple changed keys (from cascading), we need to get all affected actions
  let actions;
  if (allChangedKeys && allChangedKeys.length > 1) {
    // Collect actions for all changed keys
    const allActions = [];
    const seenRuleIds = new Set();
    for (const key of allChangedKeys) {
      const keyActions = conditionRulesEngine.getActionsToApply(state, key);
      for (const action of keyActions) {
        if (!seenRuleIds.has(action.ruleId)) {
          seenRuleIds.add(action.ruleId);
          allActions.push(action);
        }
      }
    }
    actions = allActions;
  } else {
    actions = conditionRulesEngine.getActionsToApply(state, changedElementKey);
  }

  const elementDesiredState = {};
  const optionDesiredState = {};
  const statementDesiredState = {};

  for (const actionResult of actions) {
    const { shouldApply, action, scope, targetElementIds, targetOptionInfo, targetStatementInfo } = actionResult;

    // Determine effective scope based on what targets are actually provided
    // This handles cases where scope is 'question' but statement/option targets exist
    const effectiveScope =
      targetStatementInfo?.length > 0 ? 'statement' : targetOptionInfo?.length > 0 ? 'option' : scope;

    if (effectiveScope === 'question') {
      for (const targetElementId of targetElementIds) {
        const element = elements[targetElementId];
        if (!element) continue;

        if (!elementDesiredState[targetElementId]) {
          elementDesiredState[targetElementId] = {
            shouldBeVisible: false,
            shouldBeHidden: false,
            shouldBeRequired: false,
            hasVisibilityRule: false,
            hasRequiredRule: false,
          };
        }

        const desired = elementDesiredState[targetElementId];

        switch (action) {
          case TRIGGER_MAKE_VISIBLE:
            desired.hasVisibilityRule = true;
            if (shouldApply) {
              desired.shouldBeVisible = true;
            }
            break;

          case TRIGGER_MAKE_INVISIBLE:
            desired.hasVisibilityRule = true;
            if (shouldApply) {
              desired.shouldBeHidden = true;
            }
            break;

          case TRIGGER_MAKE_REQUIRED:
            desired.hasRequiredRule = true;
            if (shouldApply) {
              desired.shouldBeRequired = true;
            }
            break;
        }
      }
    } else if (effectiveScope === 'option') {
      for (const targetOpt of targetOptionInfo) {
        const { elementId, optionId } = targetOpt;
        const element = elements[elementId];
        if (!element || !element.question?.options) continue;

        const key = `${elementId}:${optionId}`;
        if (!optionDesiredState[key]) {
          optionDesiredState[key] = {
            elementId,
            optionId,
            shouldBeVisible: false,
            shouldBeHidden: false,
            hasRule: false,
          };
        }

        const desired = optionDesiredState[key];
        desired.hasRule = true;

        switch (action) {
          case TRIGGER_MAKE_VISIBLE:
            if (shouldApply) {
              desired.shouldBeVisible = true;
            }
            break;

          case TRIGGER_MAKE_INVISIBLE:
            if (shouldApply) {
              desired.shouldBeHidden = true;
            }
            break;
        }
      }
    } else if (effectiveScope === 'statement') {
      for (const targetStmt of targetStatementInfo || []) {
        const { elementId, statementId } = targetStmt;
        const element = elements[elementId];
        if (!element || !element.question?.statements) continue;

        const key = `${elementId}:${statementId}`;
        if (!statementDesiredState[key]) {
          statementDesiredState[key] = {
            elementId,
            statementId,
            shouldBeVisible: false,
            shouldBeHidden: false,
            shouldBeRequired: false,
            hasRule: false,
            hasShowRule: false,
            hasRequiredRule: false,
          };
        }

        const desired = statementDesiredState[key];

        switch (action) {
          case TRIGGER_MAKE_VISIBLE:
            desired.hasRule = true;
            desired.hasShowRule = true;
            if (shouldApply) {
              desired.shouldBeVisible = true;
            }
            break;

          case TRIGGER_MAKE_INVISIBLE:
            desired.hasRule = true;
            if (shouldApply) {
              desired.shouldBeHidden = true;
            }
            break;

          case TRIGGER_MAKE_REQUIRED:
            desired.hasRequiredRule = true;
            if (shouldApply) {
              desired.shouldBeRequired = true;
            }
            break;
        }
      }
    }
  }

  for (const [elementId, desired] of Object.entries(elementDesiredState)) {
    const element = elements[elementId];
    const question = questions[elementId];
    if (!element) continue;

    let finalVisible = element.isVisible;

    if (desired.hasVisibilityRule) {
      if (desired.shouldBeHidden) {
        finalVisible = false;
      } else if (desired.shouldBeVisible) {
        finalVisible = true;
      } else {
        // No visibility condition is currently met - element should return to default state
        // Default to false (hidden) if defaultVisible is not explicitly true
        // This ensures elements shown by condition rules become hidden when conditions are no longer met
        finalVisible = element.defaultVisible === true;
      }
    }

    if (finalVisible !== element.isVisible) {
      yield put(updateElement({ elementId, isVisible: finalVisible }));

      if (!finalVisible && question && question.response !== null && question.response !== undefined) {
        yield put(setQuestion({ questionId: elementId, response: null, isValid: true, hasRequiredError: false }));
        // Track this element for cascading re-evaluation
        if (element.key) {
          hiddenElementKeys.push(element.key);
        }
      }
    }

    if (desired.hasRequiredRule && question) {
      const finalRequired = desired.shouldBeRequired ? true : question.defaultRequired || false;
      if (finalRequired !== question.isRequired) {
        yield put(setQuestion({ questionId: elementId, isRequired: finalRequired }));
      }
    }
  }

  const optionUpdatesByElement = {};
  const elementsWithShowOptionRules = {};
  const elementsWithAnyOptionRules = {};

  for (const [key, desired] of Object.entries(optionDesiredState)) {
    const { elementId, optionId } = desired;
    const element = elements[elementId];
    if (!element || !element.question?.options) continue;

    const option = element.question.options.find((o) => o.id === optionId);
    if (!option) continue;

    if (!elementsWithAnyOptionRules[elementId]) {
      elementsWithAnyOptionRules[elementId] = { hasAnyShowRule: false, optionIds: [] };
    }
    elementsWithAnyOptionRules[elementId].optionIds.push(optionId);

    if (desired.shouldBeVisible) {
      elementsWithAnyOptionRules[elementId].hasAnyShowRule = true;
      if (!elementsWithShowOptionRules[elementId]) {
        elementsWithShowOptionRules[elementId] = [];
      }
      elementsWithShowOptionRules[elementId].push(optionId);
    }

    let finalVisible = option.isVisible !== undefined ? option.isVisible : true;

    if (desired.hasRule) {
      if (desired.shouldBeHidden) {
        finalVisible = false;
      } else if (desired.shouldBeVisible) {
        finalVisible = true;
      } else {
        // No visibility condition is currently met - option should return to default state
        // Default to false (hidden) if defaultVisible is not explicitly true
        finalVisible = option.defaultVisible === true;
      }
    }

    if (!optionUpdatesByElement[elementId]) {
      optionUpdatesByElement[elementId] = { updates: {}, hiddenOptions: [], shownOptions: [] };
    }

    const question = questions[elementId];
    const responseArray = Array.isArray(question?.response) ? question.response : [question?.response];
    const isSelected = responseArray.includes(optionId);

    const isChangedElementTarget = elementIsTargetedByChangedElement(conditionRules, changedElementKey, element.key);
    const shouldPreserveSelectedOption =
      shouldPreserveSelectedOptions &&
      (!changedElementKey || element.key === changedElementKey || !isChangedElementTarget);

    if (shouldPreserveSelectedOption && !finalVisible && isSelected && !desired.shouldBeHidden) {
      finalVisible = true;
    }

    if (finalVisible !== (option.isVisible !== undefined ? option.isVisible : true)) {
      optionUpdatesByElement[elementId].updates[optionId] = finalVisible;
      if (!finalVisible) {
        optionUpdatesByElement[elementId].hiddenOptions.push(optionId);
      } else {
        optionUpdatesByElement[elementId].shownOptions.push(optionId);
      }
    } else if (desired.shouldBeVisible) {
      optionUpdatesByElement[elementId].shownOptions.push(optionId);
    }
  }

  // Handle hidden questions that have "show option" rules
  for (const [elementId, shownOptionIds] of Object.entries(elementsWithShowOptionRules)) {
    const element = elements[elementId];
    if (!element || element.isVisible) continue;

    if (!optionUpdatesByElement[elementId]) {
      optionUpdatesByElement[elementId] = { updates: {}, hiddenOptions: [], shownOptions: shownOptionIds };
    }

    for (const opt of element.question.options) {
      if (!shownOptionIds.includes(opt.id)) {
        optionUpdatesByElement[elementId].updates[opt.id] = false;
        optionUpdatesByElement[elementId].hiddenOptions.push(opt.id);
      }
    }
  }

  // Handle questions that were shown due to option rules but now have NO active show rules
  // They should be hidden again (return to default state)
  for (const [elementId, ruleInfo] of Object.entries(elementsWithAnyOptionRules)) {
    const element = elements[elementId];
    if (!element) continue;

    const wasOriginallyHidden = element.defaultVisible === false;
    const isCurrentlyVisible = element.isVisible;
    const hasNoActiveShowRules = !ruleInfo.hasAnyShowRule;

    if (wasOriginallyHidden && isCurrentlyVisible && hasNoActiveShowRules) {
      yield put(updateElement({ elementId, isVisible: false }));

      const restoredOptions = element.question.options.map((opt) => ({
        ...opt,
        isVisible: opt.defaultVisible !== undefined ? opt.defaultVisible : true,
      }));
      yield put(updateElement({ elementId, question: { ...element.question, options: restoredOptions } }));

      const question = questions[elementId];
      if (question && question.response !== null && question.response !== undefined) {
        yield put(setQuestion({ questionId: elementId, response: null, isValid: true, hasRequiredError: false }));
        // Track this element for cascading re-evaluation
        if (element.key) {
          hiddenElementKeys.push(element.key);
        }
      }

      delete optionUpdatesByElement[elementId];
    }
  }

  for (const [elementId, data] of Object.entries(optionUpdatesByElement)) {
    const element = elements[elementId];
    const question = questions[elementId];

    // If options are being shown and the parent question is hidden,
    // make the question visible and hide all other options that aren't being shown
    const shouldShowHiddenQuestion = data.shownOptions.length > 0 && !element.isVisible;
    if (shouldShowHiddenQuestion) {
      yield put(updateElement({ elementId, isVisible: true }));
    }

    const updatedOptions = element.question.options.map((opt) => {
      if (data.updates[opt.id] !== undefined) {
        return { ...opt, isVisible: data.updates[opt.id] };
      }
      if (shouldShowHiddenQuestion && !data.shownOptions.includes(opt.id)) {
        return { ...opt, isVisible: false };
      }
      if (shouldShowHiddenQuestion && data.shownOptions.includes(opt.id)) {
        return { ...opt, isVisible: true };
      }
      return opt;
    });

    yield put(updateElement({ elementId, question: { ...element.question, options: updatedOptions } }));

    if (question && question.response && data.hiddenOptions.length > 0) {
      const responseArray = Array.isArray(question.response) ? question.response : [question.response];
      const newResponse = responseArray.filter((r) => !data.hiddenOptions.includes(r));
      if (newResponse.length !== responseArray.length) {
        yield put(
          setQuestion({ questionId: elementId, response: newResponse.length ? newResponse : null, isValid: true }),
        );
      }
    }
  }

  const statementUpdatesByElement = {};
  const elementsWithShowStatementRules = {};
  const elementsWithAnyStatementRules = {};

  for (const [key, desired] of Object.entries(statementDesiredState)) {
    const { elementId, statementId } = desired;
    const element = elements[elementId];
    if (!element || !element.question?.statements) continue;

    const statement = element.question.statements.find((s) => s.id === statementId);
    if (!statement) continue;

    if (!elementsWithAnyStatementRules[elementId]) {
      elementsWithAnyStatementRules[elementId] = {
        hasShowRule: false,
        hasActiveShowRule: false,
        statementIds: [],
      };
    }
    const statementRuleInfo = elementsWithAnyStatementRules[elementId];
    statementRuleInfo.statementIds.push(statementId);
    statementRuleInfo.hasShowRule = statementRuleInfo.hasShowRule || desired.hasShowRule;

    if (desired.shouldBeVisible) {
      statementRuleInfo.hasActiveShowRule = true;
      if (!elementsWithShowStatementRules[elementId]) {
        elementsWithShowStatementRules[elementId] = [];
      }
      elementsWithShowStatementRules[elementId].push(statementId);
    }

    let finalVisible = statement.isVisible !== undefined ? statement.isVisible : true;

    if (desired.hasRule) {
      if (desired.shouldBeHidden) {
        finalVisible = false;
      } else if (desired.shouldBeVisible) {
        finalVisible = true;
      } else {
        // No visibility condition is currently met - statement should return to default state
        finalVisible = statement.defaultVisible !== undefined ? statement.defaultVisible : true;
      }
    }

    let finalRequired = statement.isRequired !== undefined ? statement.isRequired : undefined;
    if (desired.hasRequiredRule) {
      finalRequired = desired.shouldBeRequired ? true : statement.defaultRequired || false;
    }

    if (!statementUpdatesByElement[elementId]) {
      statementUpdatesByElement[elementId] = {
        updates: {},
        hiddenStatements: [],
        shownStatements: [],
        requiredUpdates: {},
      };
    }

    if (desired.hasRequiredRule && finalRequired !== statement.isRequired) {
      statementUpdatesByElement[elementId].requiredUpdates[statementId] = finalRequired;
    }

    if (finalVisible !== (statement.isVisible !== undefined ? statement.isVisible : true)) {
      statementUpdatesByElement[elementId].updates[statementId] = finalVisible;
      if (!finalVisible) {
        statementUpdatesByElement[elementId].hiddenStatements.push(statementId);
      }
    }

    if (desired.shouldBeVisible) {
      statementUpdatesByElement[elementId].shownStatements.push(statementId);
    }
  }

  // Handle hidden questions that have "show statement" rules
  for (const [elementId, shownStatementIds] of Object.entries(elementsWithShowStatementRules)) {
    const element = elements[elementId];
    if (!element || element.isVisible) continue;

    if (!statementUpdatesByElement[elementId]) {
      statementUpdatesByElement[elementId] = {
        updates: {},
        hiddenStatements: [],
        shownStatements: shownStatementIds,
        requiredUpdates: {},
      };
    }

    for (const stmt of element.question.statements) {
      if (!shownStatementIds.includes(stmt.id)) {
        statementUpdatesByElement[elementId].updates[stmt.id] = false;
        statementUpdatesByElement[elementId].hiddenStatements.push(stmt.id);
      }
    }
  }

  // Handle questions that were shown due to statement rules but now have NO active show rules
  // They should be hidden again (return to default state)
  for (const [elementId, ruleInfo] of Object.entries(elementsWithAnyStatementRules)) {
    const element = elements[elementId];
    if (!element) continue;

    const wasOriginallyHidden = element.defaultVisible === false;
    const isCurrentlyVisible = element.isVisible;
    const hasNoActiveStatementShowRules = !ruleInfo.hasActiveShowRule;
    const questionVisibility = elementDesiredState[elementId];
    const isKeptVisibleByQuestionRule = questionVisibility?.shouldBeVisible && !questionVisibility.shouldBeHidden;

    if (
      wasOriginallyHidden &&
      isCurrentlyVisible &&
      ruleInfo.hasShowRule &&
      hasNoActiveStatementShowRules &&
      !isKeptVisibleByQuestionRule
    ) {
      yield put(updateElement({ elementId, isVisible: false }));

      const restoredStatements = element.question.statements.map((stmt) => ({
        ...stmt,
        isVisible: stmt.defaultVisible !== undefined ? stmt.defaultVisible : true,
      }));
      yield put(updateElement({ elementId, question: { ...element.question, statements: restoredStatements } }));

      const question = questions[elementId];
      if (question && question.response !== null && question.response !== undefined) {
        yield put(setQuestion({ questionId: elementId, response: null, isValid: true, hasRequiredError: false }));
        // Track this element for cascading re-evaluation
        if (element.key) {
          hiddenElementKeys.push(element.key);
        }
      }

      delete statementUpdatesByElement[elementId];
    }
  }

  for (const [elementId, data] of Object.entries(statementUpdatesByElement)) {
    const element = elements[elementId];
    const question = questions[elementId];

    const wasOriginallyHidden = element.defaultVisible === false;
    const isHiddenByQuestionRule = elementDesiredState[elementId]?.shouldBeHidden;

    const shouldShowHiddenQuestion = data.shownStatements.length > 0 && !element.isVisible && !isHiddenByQuestionRule;
    if (shouldShowHiddenQuestion) {
      yield put(updateElement({ elementId, isVisible: true }));
    }

    const shouldManageStatementVisibility = wasOriginallyHidden && data.shownStatements.length > 0;

    const updatedStatements = element.question.statements.map((stmt) => {
      let updated = stmt;

      if (data.updates[stmt.id] !== undefined) {
        updated = { ...updated, isVisible: data.updates[stmt.id] };
      } else if (shouldManageStatementVisibility && !data.shownStatements.includes(stmt.id)) {
        updated = { ...updated, isVisible: false };
      } else if (shouldShowHiddenQuestion && data.shownStatements.includes(stmt.id)) {
        updated = { ...updated, isVisible: true };
      }

      if (data.requiredUpdates && data.requiredUpdates[stmt.id] !== undefined) {
        updated = { ...updated, isRequired: data.requiredUpdates[stmt.id] };
      }
      return updated !== stmt ? updated : stmt;
    });
    yield put(updateElement({ elementId, question: { ...element.question, statements: updatedStatements } }));

    if (question && question.response && data.hiddenStatements.length > 0 && Array.isArray(question.response)) {
      const newResponse = question.response.filter((r) => {
        if (typeof r === 'object' && r.statementId) {
          return !data.hiddenStatements.includes(r.statementId);
        }
        return true;
      });
      if (newResponse.length !== question.response.length) {
        yield put(
          setQuestion({ questionId: elementId, response: newResponse.length ? newResponse : null, isValid: true }),
        );
      }
    }
  }

  return hiddenElementKeys;
}

export function* setFirstResponseTimeIfRequired(state) {
  const { firstResponseTime } = state.flow;
  if (!firstResponseTime) {
    yield put(setFirstResponseTime(Date.now()));
  }
}

export function* saveIncompleteSurveySaga() {
  try {
    const state = yield select();

    const feedback = getFeedbackFromState(state, true);

    if (!feedback.responses.length) {
      return;
    }

    yield call(submitFeedbackSaga, { feedback, isIncomplete: true });
  } catch (error) {
    if (shouldReport(error)) {
      SentryReport({ error });
    }
  }
}

export function* changeResponseSaga({
  payload: {
    questionId,
    newResponseParams: {
      response,
      isValid = true,
      optionCount,
      isPreloadedResponse,
      skipAutoScroll = false,
      skipStateChange = false,
      skipIncompleteSave = false,
      ...others
    },
  },
}) {
  let state = yield select();

  const element = state.flow.elements[questionId];
  const { isVisible } = element;
  const changedElementKey = element?.key;

  yield put(setQuestion({ questionId, response, isValid, ...others }));
  yield setFirstResponseTimeIfRequired(state);
  state = yield select();

  yield applyConditionRules(state, changedElementKey);
  state = yield select();

  const updatedElement = state.flow.elements[questionId];
  const updatedQuestion = state.flow.questions[questionId];

  const isMatrix = updatedElement?.question?.style === MATRIX;
  const isMatrixComplete = isMatrix ? isMatrixResponseComplete(updatedElement, response, { optionCount }) : true;

  const hasStatementLevelRequired =
    isMatrix && updatedElement.question.statements?.some((s) => s.isRequired === true && s.isVisible !== false);

  let requiredStatementsComplete = true;
  if (hasStatementLevelRequired) {
    const responseArray = Array.isArray(response) ? response : [];
    const answeredStatementIds = new Set(
      responseArray.filter((r) => r && typeof r === 'object' && r.statementId).map((r) => r.statementId),
    );
    const requiredStatements = updatedElement.question.statements.filter(
      (s) => s.isRequired === true && s.isVisible !== false,
    );
    requiredStatementsComplete = requiredStatements.every((stmt) => answeredStatementIds.has(stmt.id));
  }

  const hasRequiredError = isMatrix
    ? (updatedQuestion.isRequired && updatedQuestion.hasRequiredError && !isMatrixComplete) ||
      (hasStatementLevelRequired && !requiredStatementsComplete)
    : updatedQuestion.isRequired &&
      (!response ||
        (Array.isArray(response) && response.length === 0) ||
        (Array.isArray(response) && optionCount && response.length < optionCount));

  if (hasRequiredError !== updatedQuestion.hasRequiredError) {
    yield put(setQuestion({ questionId, hasRequiredError }));
    state = yield select();
  }

  // Update dependent dynamic multiple choice questions that use options_source with selected options from the source
  try {
    const sourceQuestionId = state.flow.elements[questionId]?.question?.id;
    const dependentElementIds = Object.keys(state.flow.elements).filter((elId) => {
      const el = state.flow.elements[elId];
      const optionsSource = el?.question?.extra?.options_source;

      return (
        el?.type === QUESTION &&
        el?.question?.style === DYNAMIC_MULTIPLE_CHOICE &&
        optionsSource &&
        optionsSource === sourceQuestionId
      );
    });

    if (dependentElementIds.length) {
      const optionsToAdd = {};

      dependentElementIds.forEach((targetId) => {
        const optionsSource = state.flow.elements[targetId].question.extra.options_source;
        const generated = getOptionsFromSourceQuestion(optionsSource, state.flow.elements, state.flow.questions);
        const existing = state.flow.elements[targetId].question.options || [];

        // Keep options fed from query string, replace previous dynamic ones with the newly generated set
        const staticOptions = existing.filter((opt) => !opt.dynamic);
        optionsToAdd[targetId] = [...staticOptions, ...generated];
      });
      if (Object.keys(optionsToAdd).length) {
        yield call(addQuestionElementOptions, state.flow, optionsToAdd);
        state = yield select();

        // Clear responses in dependent questions if selected options are no longer available
        for (const targetId of dependentElementIds) {
          const currentResponse = state.flow.questions[targetId]?.response;

          if (currentResponse) {
            const newOptions = optionsToAdd[targetId];
            const newOptionIds = newOptions.map((opt) => opt.id);

            let updatedResponse = null;

            if (Array.isArray(currentResponse)) {
              const filteredResponse = currentResponse.filter((optId) => newOptionIds.includes(optId));
              updatedResponse = filteredResponse.length > 0 ? filteredResponse : null;
            } else if (!newOptionIds.includes(currentResponse)) {
              updatedResponse = null;
            }

            if (
              updatedResponse !== currentResponse &&
              (updatedResponse === null || updatedResponse.length !== currentResponse.length)
            ) {
              yield call(changeResponseSaga, {
                payload: {
                  questionId: targetId,
                  newResponseParams: {
                    response: updatedResponse,
                    isValid: true,
                    skipIncompleteSave: true,
                  },
                },
              });

              state = yield select();
            }
          }
        }
      }
    }
  } catch (e) {
    console.warn('Error occurred while setting options from source question:', e);
  }

  if (!skipStateChange && !hasStateNextPageButton(state) && isVisible) {
    const wasFlowReplaced = yield nextStateSaga({
      payload: {
        stateChangeParams: {
          source: 'question_saga',
          hasNextStateButton: false,
        },
      },
    });

    if (wasFlowReplaced) return;
  }

  if (AutoScrollElement.AUTO_SCROLL_QUESTION_TYPES.includes(element.question.style) && !skipAutoScroll) {
    const elements = Object.values(state.flow.elements)
      .filter(
        (el) =>
          el.type === 'question' &&
          el.isVisible &&
          el.question.style !== 'hidden_validation' &&
          el.stateId === state.flow.currentState.id,
      )
      .sort((a, b) => a.order - b.order);

    const currentIdx = elements.findIndex((el) => el.id === element.id);
    const nextElement = elements[currentIdx + 1];

    if (nextElement) {
      setTimeout(() => scrollToTargetElement(nextElement.id), AutoScrollElement.AUTO_SCROLL_DELAY_MS);
    }
  }

  if (state.flow.allowSavingIncompleteSurvey) {
    const nextState = findNextStateInfo(state);
    const isNextStateThankOrChat = nextState.style === THANK || nextState.style === CHAT;

    // Save incomplete survey if allowed and if it is not a preloaded response.
    // If it is last state and there is no next page button, make sure all questions are not answered in last state.
    const shouldSaveIncompleteSurvey =
      !isPreloadedResponse &&
      (!hasStateNextPageButton(state) && isNextStateThankOrChat
        ? !isQuestionsAnswered(state, hasStateNextPageButton(state))
        : true);

    if (shouldSaveIncompleteSurvey && !skipIncompleteSave) {
      yield put(saveIncompleteSurveyAction());
    }
  }
}

function uploadAttachmentEmitter(uploadUrl, file) {
  const data = new FormData();
  data.append('file', file);

  // Create an emitter to emit data on progress.
  let emit;
  const chan = eventChannel((emitter) => {
    emit = emitter;
    return () => {};
  });

  let lastEmittedProgress = -1;
  const options = {
    method: 'POST',
    body: data,
    progress: (e) => {
      const progress = Math.round((e.loaded / e.total) * 100);
      if (progress !== lastEmittedProgress) {
        lastEmittedProgress = progress;
        emit(progress);
      }
    },
  };

  const promise = requestJson(uploadUrl, options).finally(() => emit(END));

  return { promise, chan };
}

function* progressListener(questionId, channel) {
  while (true) {
    const progress = yield take(channel);
    yield put(setQuestion({ questionId, progress }));
  }
}

export function* uploadAttachmentSaga({ payload: { questionId, file } }) {
  // Upload results and progress belong only to the flow that started them.
  yield race({
    flowReplaced: take([LOAD_FLOW_SUCCESS, RESET_FLOW]),
    fileRemoved: take((action) => action.type === REMOVE_FILE && action.payload.questionId === questionId),
    upload: call(uploadAttachment, questionId, file),
  });
}

function* uploadAttachment(questionId, file) {
  const previousFile = (yield select()).flow.questions[questionId]?.file ?? null;

  yield put(
    setQuestion({
      questionId,
      isLoading: true,
      error: null,
      file: { name: file.name, size: formatBytes(file.size), type: file.type },
    }),
  );
  let upload;
  try {
    assertOnline();
    upload = uploadAttachmentEmitter(`${API_URL}/v2/storage/feedback_uploads`, file);
    yield fork(progressListener, questionId, upload.chan);
    const uploadedFileActiveStorage = yield call(identity, upload.promise);
    yield call(changeResponseSaga, {
      payload: {
        questionId,
        newResponseParams: {
          response: uploadedFileActiveStorage.data.filename,
          active_storage_blob_id: uploadedFileActiveStorage.data.id,
          isLoading: false,
          isValid: true,
        },
      },
    });
  } catch (error) {
    if (isConnectionError(error)) {
      // Nothing was uploaded, so the previous answer and its validity stay as they were.
      yield put(setQuestion({ questionId, isLoading: false, file: previousFile, error }));
    } else {
      yield call(changeResponseSaga, {
        payload: {
          questionId,
          newResponseParams: {
            error,
            isLoading: false,
            isValid: false,
          },
        },
      });
    }
  } finally {
    upload?.chan.close();
  }
}

export function* setPhoneVerificationDataSaga({ payload: { questionId, isValid, phoneNumber } }) {
  yield put(
    setQuestion({
      questionId,
      isValid,
      phoneNumber,
      error: null,
    }),
  );
}

export function* sendVerificationTokenSaga({ payload: { verificationParams } }) {
  if (!verificationParams.isValid || !verificationParams.address) {
    yield put(
      setQuestion({
        questionId: verificationParams.questionId,
        isValid: false,
      }),
    );
  } else {
    yield put(setQuestion({ questionId: verificationParams.questionId, isLoading: true, error: null }));
    const state = yield select();
    const questionId = state.flow.elements[verificationParams.questionId].question.id;
    const smsUrl = `${API_URL}/v1/customers/send_verification_token`;
    const data = new FormData();
    data.append('address', verificationParams.address);
    data.append('kind', 'sms');
    data.append('question_id', questionId);
    const options = {
      method: 'POST',
      body: data,
    };
    try {
      assertOnline();
      const verification = yield call(requestJson, smsUrl, options);
      yield put(
        setQuestion({
          questionId: verificationParams.questionId,
          hasRequiredError: false,
          isValid: true,
          isLoading: false,
          phoneNumber: {
            ...state.flow.questions[verificationParams.questionId].phoneNumber,
            verificationId: verification.id,
          },
        }),
      );
    } catch (error) {
      yield put(setQuestion({ questionId: verificationParams.questionId, isLoading: false, error }));
    }
  }
}

export function* verifyPhoneNumberSaga({ payload: { verifyParams } }) {
  if (!verifyParams.token) {
    yield put(
      setQuestion({
        questionId: verifyParams.questionId,
        hasRequiredError: true,
      }),
    );
  } else {
    yield put(setQuestion({ questionId: verifyParams.questionId, isLoading: true, error: null }));
    const verificationUrl = `${API_URL}/v1/customers/verify`;
    const data = new FormData();
    data.append('verification_id', verifyParams.verificationId);
    data.append('token', verifyParams.token);
    const options = {
      method: 'POST',
      body: data,
    };
    try {
      assertOnline();
      const verificationData = yield call(requestJson, verificationUrl, options);
      yield put(
        setQuestion({
          questionId: verifyParams.questionId,
          response: verificationData.address,
          token: verificationData.token,
          isValid: true,
          isLoading: false,
          hasRequiredError: false,
        }),
      );
    } catch (error) {
      yield put(
        setQuestion({
          questionId: verifyParams.questionId,
          // A connection failure says nothing about the code; the phone number stays valid.
          ...(isConnectionError(error) ? {} : { isValid: false }),
          isLoading: false,
          hasRequiredError: false,
          error,
        }),
      );
    }
  }
}

function* doBindingModelRequestSaga(questionId, questionIdConst, questionDataObj) {
  const dynamicRequestUrl = `${PPROXY_SERVER_URL}/integration`;
  const data = {
    reference_id: questionIdConst,
    body: questionDataObj,
  };
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };

  const result = yield call(requestJson, dynamicRequestUrl, options);
  yield put(setQuestion({ questionId, bindingData: result }));
}

function* doFlowRequestSaga(state, questionId, questionIdConst, answer, flowId) {
  const dynamicRequestUrl = `${API_URL}/v1/flows/${flowId}/requests`;
  const data = {
    question_id: questionIdConst,
    flow_id: flowId,
    answer,
  };
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };

  const result = yield call(requestJson, dynamicRequestUrl, options);
  for (let i = 0; i < result.responses.length; i++) {
    const response = result.responses[i];
    const id = findKey(state.flow.elements, {
      question: {
        id: response.question_id,
      },
    });
    yield put(setQuestion({ questionId: id, response: response.input }));
  }
}

export function* doDynamicRequestSaga({ payload: { questionId, answer } }) {
  try {
    yield put(
      setQuestion({
        questionId,
        response: answer,
        isLoading: true,
        error: null,
      }),
    );

    assertOnline();

    const state = yield select();
    const { question } = state.flow.elements[questionId];
    const questionIdConst = question.id;
    const { bindingModel } = question;

    const questionDataObj = Object.keys(state.flow.elements)
      .filter((element) => state.flow.elements[element]?.question?.key)
      .reduce((acc, element) => {
        const { key } = state.flow.elements[element].question;
        const { response } = state.flow.questions[element];
        acc[key] = response;
        return acc;
      }, {});

    if (bindingModel) {
      yield call(doBindingModelRequestSaga, questionId, questionIdConst, questionDataObj);
    } else {
      yield call(doFlowRequestSaga, state, questionId, questionIdConst, answer, state.flow.id);
    }

    yield put(setQuestion({ questionId, isLoading: false }));
  } catch (error) {
    yield put(
      setQuestion({
        questionId,
        error,
        isLoading: false,
      }),
    );
  }
}
